import type { ICellRendererAngularComp } from '@ag-grid-community/angular';
import type { ICellRendererParams } from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import type { UserRecord } from '@cdw-ae/common-data';
import type { UserProfileCardData } from '../common-ui-components-user-profile-card/common-ui-components-user-profile-card.component';
import { UserHoverCardService } from '../services/user-hover-card.service';

@Component({
  selector: 'user-cell-renderer',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './user-cell-renderer.component.html',
  styleUrl: './user-cell-renderer.component.scss',
})
export class UserCellRendererComponent implements ICellRendererAngularComp {
  private hoverCardService = inject(UserHoverCardService);

  public userName = '';
  public userId: number | string | undefined;
  public userProfileData!: UserProfileCardData;
  public onUserClickCallback:
    | ((userId: number | string, userData: UserProfileCardData) => void)
    | undefined;
  public hasClickCallback = false;
  public iconColor?: string;

  agInit(params: ICellRendererParams): void {
    // Get user ID and name from params
    // Support multiple data structures:
    // 1. Direct user data (params.data is UserRecord with id, name, email)
    // 2. Nested user data (params.data has userId + user object)
    const userData = params.data as UserRecord;
    // biome-ignore lint/suspicious/noExplicitAny: Need to access dynamic user object properties
    const userObj = (params.data as any)?.user;

    if (userData?.id && !params.data?.userId) {
      // Case 1: Row data itself is a UserRecord (e.g., user management grids)
      this.userId = userData.id;
      this.userName = params.value || userData.name || '';
    } else {
      // Case 2: Row has userId field and nested user object (e.g., audit logs)
      // Use userId from the row data (the foreign key), not user.id
      this.userId = params.data?.userId || userObj?.id;
      this.userName =
        params.value ||
        userObj?.name ||
        `User ${this.userId || 'Unknown'}`;
    }

    // Build profile card data
    this.userProfileData = {
      photoURL: undefined,
      name: userData?.name || userObj?.name || this.userName,
      title: userObj?.role || 'User',
      email: userData?.email || userObj?.email || undefined,
      phone: undefined,
    };

    // Get the click callback from cellRendererParams first, then fall back to context
    // biome-ignore lint/suspicious/noExplicitAny: AG Grid params can have custom properties
    const cellParams = params as any;

    const callback = cellParams.onUserClick || params.context?.onUserClick;
    if (callback) {
      this.onUserClickCallback = callback;
      this.hasClickCallback = true;
    }

    // Get icon color from cellRendererParams if provided
    this.iconColor = cellParams.iconColor;
  }

  onMouseEnter(event: MouseEvent): void {
    this.hoverCardService.showCard(
      this.userProfileData,
      event.clientX,
      event.clientY
    );
  }

  onMouseLeave(): void {
    this.hoverCardService.hideCard();
  }

  onMouseMove(event: MouseEvent): void {
    this.hoverCardService.updatePosition(event.clientX, event.clientY);
  }

  onUserClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.onUserClickCallback && this.userId) {
      this.onUserClickCallback(this.userId, this.userProfileData);
    }
  }

  refresh(params: ICellRendererParams): boolean {
    this.agInit(params);
    return true;
  }
}
